import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import ReactEChartsCore from "echarts-for-react/lib/core";
import { color } from "framer-motion";

const ChannelAttributionSwitcherChart = ({ data }) => {
    const chartRef = useRef(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const axisColor = prefersDark ? "#eee" : "#333";
    const bgColor = prefersDark ? "#1f1f1f" : "#fff";

    const chartColors = ['#91CC75', '#FAC858', '#EE6666', '#73C0DE', '#3BA272', '#FC8452', '#9A60B4', '#EA7CCC'];

    // ✅ 1. Define important channels to keep
    const mainChannels = [
        "Catalog",
        "Cpc",
        "Direct_Email",
        "Facebook",
        "Organic",
        "Postcard",
        "Referral"
    ];

    const channelColorMap = {
        'Catalog': '#91CC75',
        'Cpc': '#FAC858',
        'Direct_Email': '#EE6666',
        'Facebook': '#73C0DE',
        'Organic': '#3BA272',
        'Postcard': '#FC8452',
        'Referral': '#9A60B4',
        'Other': '#EA7CCC'
    };

    // ✅ 2. Process and group data
    const processed = {
        main: [],
        other: {
            name: "Other",
            raw: 0
        }
    };

    data.forEach(item => {
        const channel = item.channels?.trim();
        if (!channel) return;

        const markov = item.attribution_markov_algorithmic || 0;
        const shapley = item.attribution_shapley_size4_conv_rate_algorithmic || 0;
        const avg = (markov + shapley) / 2;

        if (avg === 0) return;

        if (mainChannels.includes(channel)) {
            processed.main.push({ name: channel, raw: avg });
        } else {
            processed.other.raw += avg;
        }
    });

    const mergedData = [...processed.main];
    if (processed.other.raw > 0) mergedData.push(processed.other);

    const total = mergedData.reduce((sum, item) => sum + item.raw, 0);

    const percentData = mergedData.map(item => ({
        name: item.name,
        value: parseFloat(((item.raw / total) * 100).toFixed(2)),
        raw: Math.round(item.raw)
    }));

    // ✅ 3. Convert to hierarchical format for sunburst
    const sunburstData = [
        {
            name: "Channels",
            children: percentData.map(d => ({
                name: d.name,
                value: d.value,
                raw: d.raw
            }))
        }
    ];

    // ✅ 4. Shared tooltip config
    const tooltip = {
        formatter: (info) => {
            const name = info.name ?? "";
            const value = info.value ?? 0;
            const raw = info.data?.raw ?? 0;
            return `<strong>${name}</strong><br/>Avg: ${raw.toLocaleString()}<br/>Contribution: ${value}%`;
        },
        backgroundColor: 'rgba(50, 50, 50, 0.9)',
        textStyle: { color: '#fff' },
        borderColor: 'rgba(255,255,255,0.2)',
        borderWidth: 1
    };

    // ✅ 5. Treemap Option
    const treemapOption = {
        backgroundColor: bgColor,
        tooltip,
        title: {
            text: '($) Revenue % by channel',
            left: 'center',
            top: 10,
            textStyle: {
                color: axisColor,
                fontSize: isMobile ? 14 : 18,
                fontWeight: 'bold'
            }
        },
        series: [
            {
                type: 'treemap',
                data: percentData,
                label: {
                    show: true,
                    formatter: '{b}\n{c}%',
                    fontSize: isMobile ? 10 : 12,
                    color: axisColor
                },
                itemStyle: {
                    borderColor: '#ccc',
                    borderWidth: 1,
                    gapWidth: 2
                },
                levels: [{ itemStyle: { gapWidth: 1 } }],
                roam: false,
                breadcrumb: { show: false }
            }
        ],
        color: chartColors
    };

    // ✅ 6. Sunburst Option
    const sunburstOption = {
        backgroundColor: bgColor,
        title: {
            text: '($) Revenue % by channel',
            left: 'center',
            top: 10,
            textStyle: {
                color: axisColor,
                fontSize: isMobile ? 14 : 18,
                fontWeight: 'bold'
            }
        },
        tooltip: {
            trigger: 'item',
            formatter: params => {
                return `${params.name}<br />Value: ${params.value}%`;
            }
        },
        series: [
            {
                type: 'sunburst',
                radius: ['30%', '85%'], // tighter layout
                nodeClick: false, // disables drilldown
                sort: null, // preserve data order
                data: [
                    {
                        name: 'Channels',
                        children: percentData.map(item => ({
                            name: item.name,
                            value: item.value,
                            itemStyle: {
                                color: channelColorMap[item.name] || '#ccc'
                            }
                        }))
                    }
                ],
                universalTransition: true,
                itemStyle: {
                    borderWidth: 1,
                    borderColor: 'rgba(255,255,255,0.8)'
                },
                label: {
                    show: true, // optional: can use 'inside' or 'rotate'
                    color: 'black'
                }
            }
        ],
        color: chartColors
    };


    // ✅ 7. Auto toggle chart every 3 seconds
    useEffect(() => {
        if (!data || data.length === 0) return;

        const chartInstance = chartRef.current?.getEchartsInstance();
        if (!chartInstance) return;

        let isTreemap = true;
        chartInstance.setOption(treemapOption, true);

        const interval = setInterval(() => {
            isTreemap = !isTreemap;
            chartInstance.setOption(isTreemap ? treemapOption : sunburstOption, true);
        }, 3000);

        return () => clearInterval(interval);
    }, [data]);

    return (
        <div className="p-2 sm:p-4">
            <ReactEChartsCore
                ref={chartRef}
                echarts={echarts}
                option={{}} // Initial empty option to avoid null
                notMerge={true}
                lazyUpdate={true}
                style={{ width: '100%', height: isMobile ? '400px' : '500px' }}
                opts={{ renderer: 'canvas', devicePixelRatio: window.devicePixelRatio || 1 }}
            />
        </div>
    );
};

export default ChannelAttributionSwitcherChart;
